import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../../api/client.js';
import { fmtAgo } from '../../lib/labels.js';

/**
 * Support — file an in-app support case (migration 051). Submitting emails the
 * operator inbox; the user sees their own case history below. No billing gate:
 * a past-due user must still be able to ask for help.
 */
export default function Support() {
  const qc = useQueryClient();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [msg, setMsg] = useState(null);

  const casesQ = useQuery({
    queryKey: ['support', 'cases'],
    queryFn: ({ signal }) => apiGet('/support/cases', { signal }),
  });

  const submitM = useMutation({
    mutationFn: () => apiPost('/support/cases', { subject: subject.trim(), body: body.trim() }),
    onSuccess: () => {
      setSubject('');
      setBody('');
      setMsg({ kind: 'ok', text: 'Thanks — your case was sent. We’ll reply by email.' });
      qc.invalidateQueries({ queryKey: ['support', 'cases'] });
    },
    onError: (err) => setMsg({ kind: 'err', text: err.body?.error || err.message }),
  });

  const canSubmit = subject.trim() && body.trim() && !submitM.isPending;

  return (
    <div className="account-page">
      <header className="account-page-head">
        <h1>Support</h1>
        <p className="muted">
          Something not working, or a question we can help with? Send us a case and we’ll reply by email.
        </p>
      </header>

      <section className="account-section">
        <form
          className="password-form"
          onSubmit={(e) => { e.preventDefault(); if (canSubmit) submitM.mutate(); }}
        >
          <label className="login-field">
            <span>Subject</span>
            <input
              value={subject}
              maxLength={200}
              onChange={(e) => { setSubject(e.target.value); setMsg(null); }}
              placeholder="Short summary — e.g. “Instagram won’t connect”"
              disabled={submitM.isPending}
              required
            />
          </label>
          <label className="login-field">
            <span>Description</span>
            <textarea
              rows={6}
              maxLength={5000}
              value={body}
              onChange={(e) => { setBody(e.target.value); setMsg(null); }}
              placeholder="What happened, what you expected, and any steps to reproduce."
              disabled={submitM.isPending}
              required
            />
          </label>
          {msg && <div className={`post-action-msg ${msg.kind}`}>{msg.text}</div>}
          <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
            {submitM.isPending ? 'Sending…' : 'Send support case'}
          </button>
        </form>
      </section>

      <section className="account-section">
        <h2 className="account-subhead">Your cases</h2>
        {casesQ.isLoading && <div className="loading">Loading…</div>}
        {casesQ.isError && <div className="post-action-msg err">Couldn’t load your cases: {casesQ.error.message}</div>}
        {casesQ.data && casesQ.data.length === 0 && <p className="muted small">No cases yet.</p>}
        {casesQ.data && casesQ.data.length > 0 && (
          <ul className="support-case-list">
            {casesQ.data.map((c) => (
              <li key={c.id} className="support-case-row">
                <span className="support-case-subject">{c.subject}</span>
                <span className={`support-case-status support-case-${c.status}`}>{c.status}</span>
                <span className="muted small">{fmtAgo(c.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
